import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Check, Copy } from "lucide-react";

import { Button } from "../../components/Button";
import { CustomSelector } from "../../components/CustomSelector";
import { Label } from "../../components/Label";
import { AlertNote } from "../../components/AlertNote";
import { UnifiedModal } from "../../components/UnifiedModal";

const ROLE_OPTIONS = [
  { value: "consultant", label: "Consultor" },
  { value: "admin", label: "Administrador" },
];

const ROLE_PERMISSIONS = {
  consultant:
    "Gestiona los clientes que tiene asignados: proyectos, tareas, tickets y comunicación con el cliente. No puede gestionar el equipo ni la configuración de la agencia.",
  admin:
    "Acceso completo a la agencia: gestiona consultores, clientes, proyectos y la configuración de la agencia, e invita a otros usuarios.",
};

export const InviteUserModal = ({ open, onClose, onGenerate, onCopy }) => {
  const [role, setRole] = useState("consultant");
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setLinks({});
      setCopied(false);
      return;
    }
    if (links[role]) return;
    let cancelled = false;
    setLoading(true);
    onGenerate(role)
      .then((url) => {
        if (!cancelled && url) setLinks((prev) => ({ ...prev, [role]: url }));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role]);

  const link = links[role];

  const handleCopy = async () => {
    if (await onCopy(link)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title="Invitar a un nuevo usuario"
      maxWidth="sm"
      footer={
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Box>
          <Label sx={{ mb: 1, display: "block" }}>Rol</Label>
          <CustomSelector
            value={role}
            onChange={(value) => {
              setRole(value);
              setCopied(false);
            }}
            options={ROLE_OPTIONS}
          />
        </Box>
        <Box>
          <Label sx={{ mb: 1, display: "block" }}>Enlace de invitación</Label>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                px: 1.5,
                py: 1,
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
              }}
            >
              {loading || !link ? (
                <CircularProgress size={16} />
              ) : (
                <Typography
                  variant="body2"
                  noWrap
                  title={link}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {link}
                </Typography>
              )}
            </Box>
            <Button
              onClick={handleCopy}
              disabled={!link || loading}
              sx={{ gap: 1, flexShrink: 0 }}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "¡Copiado!" : "Copiar"}
            </Button>
          </Stack>
        </Box>
        <AlertNote variant="info">{ROLE_PERMISSIONS[role]}</AlertNote>
      </Stack>
    </UnifiedModal>
  );
};

InviteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
};
